import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import useAxios from "../../../utility/axios-token-manager/init";

const PRIMARY = "#a7cc48";

const fmt = (ts) =>
  new Date(ts).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function TicketDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [ticket, setTicket]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending]   = useState(false);
  const [closing, setClosing]   = useState(false);
  const [error, setError]       = useState("");
  const bottomRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    setLoading(true);
    try {
      const res = await useAxios.get(`/admin/support-tickets/${id}`);
      if (res.data.success) setTicket(res.data.ticket);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load ticket");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  const handleReply = async () => {
    const text = replyText.trim();
    if (!text) return;
    setSending(true);
    setError("");
    try {
      const res = await useAxios.post(`/admin/support-tickets/${id}/reply`, { message: text });
      if (res.data.success) {
        setTicket(res.data.ticket);
        setReplyText("");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm("Close this ticket? The merchant will no longer be able to reply.")) return;
    setClosing(true);
    try {
      const res = await useAxios.put(`/admin/support-tickets/${id}/close`);
      if (res.data.success) setTicket((prev) => ({ ...prev, status: "closed" }));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to close ticket");
    } finally {
      setClosing(false);
    }
  };

  if (loading) return (
    <div style={S.center}><div style={S.spinner} /></div>
  );
  if (!ticket) return (
    <div style={S.center}><p style={{ color: "#ef4444" }}>Ticket not found.</p></div>
  );

  const isClosed = ticket.status === "closed";

  return (
    <div style={S.wrap}>
      {/* Back + header */}
      <div style={S.topBar}>
        <button onClick={() => history.push("/admin/support-tickets")} style={S.backBtn}>
          ← Back to Tickets
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={S.title}>{ticket.title}</h2>
          <span style={S.merchantMeta}>{ticket.merchantName} · {ticket.merchantEmail}</span>
        </div>
        <span style={{ ...S.statusBadge, ...(isClosed ? S.closedBadge : S.openBadge) }}>
          {isClosed ? "Closed" : "Open"}
        </span>
        {!isClosed && (
          <button onClick={handleClose} disabled={closing} style={S.closeBtn}>
            {closing ? "…" : "Close Ticket"}
          </button>
        )}
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {/* Chat thread */}
      <div style={S.chatBox}>
        {ticket.messages.length === 0 ? (
          <p style={{ color: "#9ca3af", textAlign: "center", marginTop: 60 }}>No messages yet.</p>
        ) : (
          ticket.messages.map((msg, idx) => {
            const isAdmin = msg.sender === "admin";
            return (
              <div key={idx} style={{ display: "flex", justifyContent: isAdmin ? "flex-end" : "flex-start", marginBottom: 14 }}>
                <div style={{ maxWidth: "68%" }}>
                  <div style={{
                    padding: "10px 14px",
                    borderRadius: isAdmin ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: isAdmin ? PRIMARY : "#fff",
                    color: isAdmin ? "#fff" : "#111827",
                    border: isAdmin ? "none" : "1px solid #e5e7eb",
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}>
                    {msg.message}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: isAdmin ? "right" : "left" }}>
                    {isAdmin ? "Support Team" : ticket.merchantName} · {fmt(msg.timestamp)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply area */}
      {isClosed ? (
        <div style={S.closedNotice}>
          This ticket is closed — no further replies can be sent.
        </div>
      ) : (
        <div style={S.replyRow}>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply… (Ctrl+Enter to send)"
            rows={3}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReply(); }}
            style={S.textarea}
          />
          <button
            onClick={handleReply}
            disabled={sending || !replyText.trim()}
            style={{ ...S.sendBtn, background: replyText.trim() ? PRIMARY : "#d1d5db", cursor: replyText.trim() ? "pointer" : "default" }}
          >
            {sending ? "…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap:        { fontFamily: "sans-serif", maxWidth: 780, margin: "0 auto", padding: "0 4px" },
  center:      { textAlign: "center", padding: "80px 0" },
  spinner:     { width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: `3px solid ${PRIMARY}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
  topBar:      { display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" },
  backBtn:     { padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  title:       { margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" },
  merchantMeta:{ color: "#6b7280", fontSize: 12 },
  statusBadge: { padding: "4px 12px", borderRadius: 20, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" },
  openBadge:   { background: "#d1fae5", color: "#065f46", border: "1px solid #6ee7b7" },
  closedBadge: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
  closeBtn:    { padding: "6px 14px", border: "1px solid #fca5a5", borderRadius: 8, background: "#fef2f2", color: "#dc2626", cursor: "pointer", fontWeight: 600, fontSize: 13 },
  errorBox:    { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#dc2626", marginBottom: 12, fontSize: 14 },
  chatBox:     { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, minHeight: 320, maxHeight: 480, overflowY: "auto", marginBottom: 14 },
  closedNotice:{ background: "#f3f4f6", borderRadius: 10, padding: "14px 16px", textAlign: "center", color: "#6b7280", fontSize: 14 },
  replyRow:    { display: "flex", gap: 10 },
  textarea:    { flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid #d1d5db", fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", minHeight: 70 },
  sendBtn:     { padding: "0 22px", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, transition: "background 0.2s" },
};
